import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Plus, Edit2, ToggleLeft, ToggleRight, ChevronRight } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, Input, Textarea, EmptyState } from '../../../components/ui'
import { Settings } from 'lucide-react'

interface Pipeline { id: number; name: string; description: string | null; color: string | null; is_active: boolean; stages: any[]; demands_count: number }

export default function DemandSettingsPage() {
  const navigate = useNavigate()
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState({ name: '', description: '', color: '#3b82f6' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => { fetchPipelines() }, [])

  async function fetchPipelines() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/demands/pipelines'))
      setPipelines(res.data.data)
    } catch { /* */ } finally { setLoading(false) }
  }

  function openCreate() {
    setEditingId(null)
    setForm({ name: '', description: '', color: '#3b82f6' })
    setErrors({})
    setModalOpen(true)
  }

  function openEdit(p: Pipeline, e: React.MouseEvent) {
    e.stopPropagation()
    setEditingId(p.id)
    setForm({ name: p.name, description: p.description || '', color: p.color || '#3b82f6' })
    setErrors({})
    setModalOpen(true)
  }

  async function handleSave() {
    if (saving) return
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Nome é obrigatório'
    setErrors(e)
    if (Object.keys(e).length > 0) return

    setSaving(true)
    try {
      if (editingId) {
        await axios.put(buildApiUrl(`/v1/demands/pipelines/${editingId}`), form)
        toast('success', 'Funil atualizado')
      } else {
        await axios.post(buildApiUrl('/v1/demands/pipelines'), form)
        toast('success', 'Funil criado')
      }
      setModalOpen(false)
      fetchPipelines()
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleToggle(id: number, e: React.MouseEvent) {
    e.stopPropagation()
    await axios.put(buildApiUrl(`/v1/demands/pipelines/${id}/toggle`))
    fetchPipelines()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/settings/general')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuração de Demandas</h1>
            <p className="text-gray-500 mt-1">Gerencie funis, etapas e categorias</p>
          </div>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>Novo Funil</Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : pipelines.length === 0 ? (
        <Card>
          <EmptyState icon={<Settings className="w-7 h-7" />} title="Nenhum funil configurado" description="Crie o primeiro funil para gerenciar demandas."
            action={<Button size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Criar funil</Button>} />
        </Card>
      ) : (
        <div className="space-y-3">
          {pipelines.map(p => (
            <Card key={p.id} padding="md">
              <div className="flex items-center justify-between cursor-pointer" onClick={() => navigate(`/settings/demands/${p.id}`)}>
                <div className="flex items-center gap-4">
                  {p.color && <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />}
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-gray-900">{p.name}</p>
                      <Badge variant={p.is_active ? 'success' : 'gray'} size="sm" dot>{p.is_active ? 'Ativo' : 'Inativo'}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{p.stages.length} etapa{p.stages.length !== 1 ? 's' : ''}</span>
                      <span>{p.demands_count} demanda{p.demands_count !== 1 ? 's' : ''}</span>
                      {p.description && <span className="hidden sm:inline">· {p.description}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={e => openEdit(p, e)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={e => handleToggle(p.id, e)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                    {p.is_active ? <ToggleRight className="w-5 h-5 text-success-500" /> : <ToggleLeft className="w-5 h-5" />}
                  </button>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal criar/editar funil */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setModalOpen(false)}>{editingId ? 'Editar Funil' : 'Novo Funil'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input label="Nome" value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors({}) }}
              placeholder="Ex: Saúde, Educação, Infraestrutura" error={errors.name} />
            <Textarea label="Descrição" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Opcional" rows={2} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Cor</label>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="h-10 w-20 rounded-lg border border-gray-300 cursor-pointer" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} loading={saving}>{editingId ? 'Salvar' : 'Criar'}</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
